import type { QueryClient } from '@tanstack/react-query';

/**
 * Invalida/refetch queries da aba Comissão.
 * A detail usa `['ploomes-deal', urlId]` (string) e os cards usam `deal.id` (number) —
 * precisa invalidar os dois formatos senão a UI não atualiza sem F5.
 */
export async function invalidateDealCommissionQueries(
    queryClient: QueryClient,
    dealId: number | string,
): Promise<void> {
    const ids = dealIdVariants(dealId);

    const leafKeys = [
        'ploomes-deal-commission-preview',
        'commission-sdr-link',
        'commission-seller-link',
        'commission-collaborator-link',
        'commission-legacy-cascade',
        'commission-manual-edits',
        'commission-rate-surfaces',
        'deal-installments',
        'ploomes-deal-quote-installments',
        'commission-reveal-log',
    ] as const;

    await Promise.all(
        ids.flatMap((id) => [
            ...leafKeys.map((key) =>
                queryClient.invalidateQueries({ queryKey: [key, id] }),
            ),
            queryClient.invalidateQueries({ queryKey: ['commissioning', 'installments', id] }),
            queryClient.invalidateQueries({ queryKey: ['ploomes-deal', id] }),
        ]),
    );

    // Relatório/histórico lê CommissionRecord — sem isso a aba do vendedor fica stale.
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['commissioning', 'seller-profile'] }),
        queryClient.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] }),
        queryClient.invalidateQueries({ queryKey: ['commissioning', 'seller-timeline'] }),
        queryClient.invalidateQueries({ queryKey: ['commissioning', 'seller-month'] }),
        queryClient.invalidateQueries({ queryKey: ['my-seller-profile'] }),
    ]);

    // Força refetch das queries ativas (não só marcar stale).
    await Promise.all(
        ids.flatMap((id) => [
            queryClient.refetchQueries({
                queryKey: ['ploomes-deal-commission-preview', id],
                type: 'active',
            }),
            queryClient.refetchQueries({
                queryKey: ['commissioning', 'installments', id],
                type: 'active',
            }),
            queryClient.refetchQueries({
                queryKey: ['ploomes-deal', id],
                type: 'active',
            }),
            queryClient.refetchQueries({
                queryKey: ['ploomes-deal-quote-installments', id],
                type: 'active',
            }),
        ]),
    );
}

function dealIdVariants(dealId: number | string): Array<number | string> {
    const raw = String(dealId).trim();
    const asNum = Number(raw);
    const out: Array<number | string> = [dealId];
    if (raw !== '') {
        out.push(raw);
    }
    if (Number.isFinite(asNum)) {
        out.push(asNum);
    }

    return [...new Set(out)];
}
